import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AccountVerification from '../components/register/AccountVerification';
import CompanyDetails from '../components/register/CompanyDetails';
import ReviewSubmit from '../components/register/ReviewSubmit';
import SetupPassword from '../components/register/SetupPassword';
import FinalOTPVerification from '../components/register/FinalOTPVerification';

const RegisterMover = () => {
  const navigate = useNavigate();
  // Steps: 0 Account Info, 1 Business Info, 2 Review, 3 Password Setup, 4 OTP
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    verificationCode: '',
    isVerified: false,
    companyName: '',
    website: '',
    businessAddress: '',
    city: '',
    state: '',
    zipCode: '',
    yearsInBusiness: '',
    employeeCount: '',
    googleMapsProfile: '',
    description: '',
    password: '',
    smsConsent: false
  });

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const nextStep = () => setCurrentStep((s) => Math.min(4, s + 1));

  const prevStep = () => setCurrentStep((s) => Math.max(0, s - 1));

  const handleSubmit = async () => {
    // Application is saved in FinalOTPVerification after OTP is verified
    // Just navigate to success page
    navigate('/registration-success');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-white pt-32 pb-8 md:pt-36 md:pb-12">
      <Helmet>
        <title>Register as a Mover – MyMoveAdvisor</title>
        <meta name="description" content="Join MyMoveAdvisor as a professional moving company. Get access to quality leads and grow your moving business." />
        <link rel="canonical" href="https://mymoveadvisor.com/register" />
        <meta property="og:title" content="Register as a Mover – MyMoveAdvisor" />
        <meta property="og:description" content="Join our network of professional movers and start receiving leads today." />
      </Helmet>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="bg-orange-500 text-white rounded-xl w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8"><rect width="16" height="13" x="2" y="6" rx="2" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="9" cy="18" r="2" /><circle cx="17" cy="18" r="2" /></svg>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              Register as Moving Company
            </h1>
            <p className="text-gray-600 mb-2">
              Step {currentStep + 1} of 5: {
                currentStep === 0 ? 'Account Information' :
                  currentStep === 1 ? 'Business Information' :
                    currentStep === 2 ? 'Review' :
                      currentStep === 3 ? 'Setup Password' : 'OTP Verification'
              }
            </p>
            {currentStep === 0 && (
              <p className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-orange-100 text-sm shadow-sm transition-all hover:shadow-md hover:border-primary/30 group">
                <span className="text-gray-500">New to MyMoveAdvisor?</span>
                <Link to="/resources" className="text-primary font-bold flex items-center gap-1 group-hover:scale-105 transition-transform">
                  View Registration Guide
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </p>
            )}
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-orange-400 to-orange-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / 5) * 100}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-yellow-200 shadow-[0_6px_24px_rgba(0,0,0,0.08)] p-6 md:p-8">
            {currentStep === 0 && (
              <AccountVerification
                data={formData}
                updateData={updateFormData}
                onNext={nextStep}
              />
            )}
            {currentStep === 1 && (
              <CompanyDetails
                data={formData}
                updateData={updateFormData}
                onNext={nextStep}
                onPrev={prevStep}
              />
            )}
            {currentStep === 2 && (
              <ReviewSubmit
                data={formData}
                updateData={updateFormData}
                onPrev={prevStep}
                onContinue={nextStep}
              />
            )}
            {currentStep === 3 && (
              <SetupPassword
                data={formData}
                updateData={updateFormData}
                onPrev={prevStep}
                onNext={nextStep}
              />
            )}
            {currentStep === 4 && (
              <FinalOTPVerification
                data={formData}
                onBack={prevStep}
                onVerified={handleSubmit}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterMover;
